import { NgClass, isPlatformBrowser } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  DOCUMENT,
  inject,
  signal,
  DestroyRef,
} from '@angular/core';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { COURSE_SECTION_SIDE_MENU_ITEMS } from './constants/side-menu.constant';
import { SecondarySideMenuI } from '../../../../shared/interfaces/secondary-side-menu.interface';
import { CourseSectionI } from '../../interfaces/course-section.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { Location } from '@angular/common';
import { CourseSectionsApiService } from '../../course-sections-api.service';
import { DelegatedUIErrorI } from '../../../../shared/interfaces/delegated-ui-error.interface';
@Component({
  selector: 'app-course-section-details-side-menu',
  imports: [TranslateModule, NgClass, RouterLink],
  templateUrl: './course-section-details-side-menu.component.html',
  styleUrl: './course-section-details-side-menu.component.scss',
})
export class CourseSectionDetailsSideMenuComponent
  implements OnInit, OnDestroy
{
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private readonly platformID: Object
  ) {}

  private readonly router = inject(Router);
  public route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly courseSectionsApiService = inject(CourseSectionsApiService);
  private readonly location = inject(Location);

  selectedCourseSection: CourseSectionI | null = null;
  selectedCourseSectionID!: string;

  sideMenuItems = COURSE_SECTION_SIDE_MENU_ITEMS;
  isSideMenuOpen = signal<boolean>(false);

  ngOnInit(): void {
    this.initScreenType();
    this.getDataFromRoute();
  }

  getDataFromRoute() {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((paramMap) => {
        const courseSectionID = paramMap.get('courseSectionID');
        if (!courseSectionID) {
          this.toastService.showError(
            this.translateService.instant('errors.contactTechnicalSupport')
          );
          this.location.back();
        } else {
          this.selectedCourseSectionID = courseSectionID;

          this.fetchCourseSection(courseSectionID);
        }
      });
  }
  fetchCourseSection(courseSectionID: string) {
    this.courseSectionsApiService
      .findOne(courseSectionID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (findOneCourseSectionResponse) => {
          const { data } = findOneCourseSectionResponse;
          this.selectedCourseSection = data;
        },
        error: (err: DelegatedUIErrorI) => {
          this.toastService.showError(err.description, err.title);
        },
      });
  }
  public getSideMenuItemIcon(sideMenuItem: SecondarySideMenuI) {
    return this.router.url.endsWith('/' + sideMenuItem.route(''))
      ? sideMenuItem.activeIcon
      : sideMenuItem.nonActiveIcon;
  }

  public isCurrentRouteActive(sideMenuItem: SecondarySideMenuI) {
    return this.router.url.endsWith(
      '/' +
        sideMenuItem.route(
          this.selectedCourseSection?._id ?? this.selectedCourseSectionID
        )
    );
  }

  public getOutlierJustificationIcon(): string {
    return this.router.url.endsWith('/outlier-justification')
      ? 'svgs/side-menu/active-evaluation-criterion.svg'
      : 'svgs/side-menu/non-active-evaluation-criterion.svg';
  }

  public isOutlierJustificationActive(): boolean {
    return this.router.url.endsWith('/outlier-justification');
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event & { target: { innerWidth: number } }) {
    this.isSideMenuOpen.update(
      () => event.target?.innerWidth <= 700 && event.target?.innerWidth >= 500
    );
  }

  initScreenType() {
    if (isPlatformBrowser(this.platformID)) {
      this.isSideMenuOpen.update(
        () =>
          this.document.defaultView!.innerWidth <= 700 &&
          this.document.defaultView!.innerWidth >= 500
      );
    }
  }

  ngOnDestroy(): void {}
}
